import { legalActions } from '@brig/rules-engine';
import { hintsFor } from './hints.ts';
import { current } from './history.ts';
import type { TurnSnapshot } from './useTurn.ts';

/** The phase of the turn the screen shows (docs/ux.md §3). */
export type UiPhase = 'handover' | 'replay' | 'waiting' | 'muster' | 'wind' | 'battle' | 'over';

export function uiPhase(snapshot: TurnSnapshot): UiPhase {
  for (const p of ['handover', 'replay', 'waiting', 'muster', 'wind', 'battle', 'over'] as const) {
    if (snapshot.matches(p)) return p;
  }
  return 'muster';
}

/**
 * What a tap on the board means now (docs/ux.md §3.1), read from the turn machine; never
 * stored. Being in the squadron, being the ship in command, having a move left and having
 * shots left are four separate things: a ship may have moved and still fire.
 * - `squadronSelection`: a tap on an own ship that may sail adds it to the squadron or takes
 *   it out (U-MUST-01);
 * - `windRolling`: the wind is shown; nothing on the board answers;
 * - `command`: the wind is known, nothing selected: a tap selects a ship to command;
 * - `shipSelected`: a unit's card is open;
 * - `movementTargeting`: the selected ship's reach is lit, a tap on it sails there;
 * - `combatTargeting`: the fire panel is open on a target;
 * - `none`: the curtain, a replay, the end of the game.
 */
export type Interaction =
  | 'squadronSelection'
  | 'windRolling'
  | 'command'
  | 'shipSelected'
  | 'movementTargeting'
  | 'combatTargeting'
  | 'none';

export function interaction(snapshot: TurnSnapshot): Interaction {
  const ctx = snapshot.context;
  const phase = uiPhase(snapshot);
  if (phase === 'wind') return 'windRolling';
  if (phase !== 'muster' && phase !== 'battle') return 'none';
  if (ctx.fire) return 'combatTargeting';
  if (ctx.selected) {
    const state = current(ctx.history);
    const h = hintsFor(state, legalActions(state, state.turn.side), ctx.selected);
    return h.moveCells.size + (ctx.portMove ? h.portCells.size : 0) > 0
      ? 'movementTargeting'
      : 'shipSelected';
  }
  return phase === 'muster' ? 'squadronSelection' : 'command';
}
